import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import "../styles/NichePage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";
import { track } from "../utils/analytics";
import Header from "../components/Header";
import ContactUs from "../components/ContactUs";
import ContactIcons from "../components/ContactIcons";
import { useEffect } from "react";

const NichePage = () => {
  const { slug } = useParams();
  const { language, text } = useLanguage();
  const isHeb = language === "he";
  const niche = text.niches[slug];
  const industry = text.industries.items.find((i) => i.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta({
    title: niche
      ? `${niche.hero.title} | NKR`
      : "NKR — Software that ships",
    description: niche ? niche.hero.lede : undefined,
  });

  useEffect(() => {
    track("niche_page_view", { slug });
    window.scrollTo(0, 0);
  }, [slug]);

  if (!niche) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <div className="header-shell">
        <Header />
      </div>

      <main className="niche-page" dir={isHeb ? "rtl" : "ltr"}>
        <Link to="/" className="niche-back">
          {text.portfolio.detail.backToHome}
        </Link>

        <section className="niche-hero">
          <span className="eyebrow">{niche.hero.eyebrow}</span>
          <h1 className="niche-hero-title">{niche.hero.title}</h1>
          <p className="niche-hero-lede">{niche.hero.lede}</p>

          <ul className="niche-bullets">
            {niche.hero.bullets.map((b) => (
              <li key={b}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="niche-ctas">
            <a className="btn btn-primary" href="#niche-contact" onClick={() => track("niche_cta_primary", { slug })}>
              {niche.hero.primaryCta}
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a className="btn btn-ghost" href="#niche-solution">
              {niche.hero.secondaryCta}
            </a>
          </div>

          {industry && (
            <div className="niche-pillbar">
              <span className="niche-pill">{industry.icon} {industry.name}</span>
              <span className="niche-pill niche-pill-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {industry.outcome}
              </span>
            </div>
          )}
        </section>

        <section className="niche-block niche-problems">
          <h2 className="niche-block-title">{niche.problems.title}</h2>
          <ul className="niche-problems-list">
            {niche.problems.items.map((p, i) => (
              <li key={i}>
                <span className="niche-x" aria-hidden="true">×</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </section>

        <section id="niche-solution" className="niche-block niche-solution">
          <h2 className="niche-block-title">{niche.solution.title}</h2>
          <div className="niche-solution-grid">
            {niche.solution.items.map((it) => (
              <div key={it.t} className="niche-solution-card">
                <h3>{it.t}</h3>
                <p>{it.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="niche-block niche-proof">
          <h2 className="niche-block-title">{niche.proof.title}</h2>
          <figure className="niche-proof-card">
            <blockquote>"{niche.proof.quote}"</blockquote>
            <figcaption>
              <strong>{niche.proof.name}</strong>
              <span>{niche.proof.role}</span>
            </figcaption>
          </figure>
        </section>

        <section className="niche-block niche-pricing">
          <h2 className="niche-block-title">{niche.pricing.title}</h2>
          <div className="niche-pricing-card">
            <div className="niche-pricing-headline">
              <span className="niche-pricing-price gradient-text">
                {niche.pricing.priceFrom}
              </span>
              <span className="niche-pricing-time">
                {niche.pricing.timeline}
              </span>
            </div>
            <ul className="niche-pricing-includes">
              {niche.pricing.includes.map((inc) => (
                <li key={inc}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
            <a
              className="btn btn-primary niche-pricing-cta"
              href="#niche-contact"
              onClick={() => track("niche_cta_pricing", { slug })}
            >
              {niche.hero.primaryCta}
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </section>

        <section className="niche-block niche-faq">
          <h2 className="niche-block-title">FAQ</h2>
          <div className="niche-faq-list">
            {niche.faq.map((item, i) => {
              const open = i === openFaq;
              return (
                <div key={i} className={`faq-item ${open ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="faq-q"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    <span>{item.q}</span>
                    <span className="faq-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                  <div className="faq-a-wrap">
                    <p className="faq-a">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div id="niche-contact">
          <ContactUs />
        </div>
      </main>

      <footer className="site-footer">
        <ContactIcons />
        <p>
          © {new Date().getFullYear()} {text.brand} · {text.footer}
        </p>
      </footer>
    </>
  );
};

export default NichePage;
